import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from '../utils/toast';
import TrademarkStep1 from './TrademarkStep1';
import TrademarkStep2 from './TrademarkStep2';
import TrademarkDocumentUpload from './TrademarkDocumentUpload';
import TrademarkSettings from './TrademarkSettings';

const TrademarkApplication = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [application, setApplication] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [settings, setSettings] = useState(null);
  const [masterDataList, setMasterDataList] = useState([]);
  const [clientTypes, setClientTypes] = useState([]);
  const [clientTypesLoading, setClientTypesLoading] = useState(true);
  const [fees, setFees] = useState({
    governmentFeesForOneTrademark: 0,
    professionalFeesForOneTrademark: 0,
    udyogAadhaarFeesForOneTrademark: 0,
    totalFeesForOneTrademark: 0,
    governmentFees: 0,
    professionalFees: 0,
    udyogAadhaarFees: 0,
    totalFees: 0
  });
  const [showInvoice, setShowInvoice] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [issueToClient, setIssueToClient] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);
  const [groupSubmissionSettings, setGroupSubmissionSettings] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [showInvoiceStep, setShowInvoiceStep] = useState(false);
  const [showPaymentStep, setShowPaymentStep] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [selectedPaymentGateway, setSelectedPaymentGateway] = useState(null);
  const [currentPayment, setCurrentPayment] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          loadApplication(),
          loadSettings(),
          loadMasterData(),
          loadClientTypes(),
          loadUserPermissions()
        ]);
      } catch (error) {
        console.error('Failed to initialize data:', error);
        toast.error('Failed to load application data');
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, [id]);

  // Load invoice data when Step 4 is accessed
  useEffect(() => {
    const loadInvoiceData = async () => {
      if (currentStep === 4 && !invoiceData && application?.status === 'submitted') {
        try {
          const response = await api.get(`/trademark-applications/${id}/invoice-data`);
          setInvoiceData(response.data.data);
        } catch (error) {
          console.error('Failed to load invoice data:', error);
          toast.error('Failed to load invoice data');
        }
      }
    };

    loadInvoiceData();
  }, [currentStep, invoiceData, application?.status, id]);

  // Reload group submission settings when settings are loaded
  useEffect(() => {
    const reloadGroupSettings = async () => {
      if (settings && userPermissions?.groupId) {
        const groupSettings = settings.groupSubmissionSettings?.find(
          setting => setting.groupId === userPermissions.groupId && setting.isActive
        );
        console.log('Reloading group settings:', {
          settings: settings.groupSubmissionSettings,
          userGroupId: userPermissions.groupId,
          foundGroupSettings: groupSettings
        });
        setGroupSubmissionSettings(groupSettings);
      }
    };

    reloadGroupSettings();
  }, [settings, userPermissions?.groupId]);

  const loadApplication = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/trademark-applications/${id}`);
      setApplication(response.data.data);


      // Preserve current step if user has already progressed further
      const applicationStep = response.data.data.currentStep || 1;
      if (currentStep > applicationStep) {
        // Keep the current step if user has progressed further
        console.log('Preserving current step:', currentStep, 'over application step:', applicationStep);
      } else {
        setCurrentStep(applicationStep);
      }
    } catch (error) {
      console.error('Failed to load application:', error);
      toast.error('Failed to load trademark application');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await api.get('/trademark-applications/settings');
      setSettings(response.data.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadMasterData = async () => {
    try {
      const response = await api.get('/master-data');
      setMasterDataList(response.data.data || []);
    } catch (error) {
      console.error('Failed to load master data:', error);
    }
  };

  const loadClientTypes = async () => {
    try {
      const response = await api.get('/admin/client-types');
      setClientTypes(response.data.data);
    } catch (error) {
      console.error('Failed to load client types:', error);
    }
  };

  const loadUserPermissions = async () => {
    try {
      console.log('🔄 Starting loadUserPermissions...');
      const response = await api.get('/auth/me');
      const user = response.data.data;
      console.log('📋 Raw user data from API:', user);
      console.log('📋 User data structure:', {
        hasRole: !!user.role,
        hasGroupId: !!user.groupId,
        hasGroupName: !!user.groupName,
        role: user.role,
        groupId: user.groupId,
        groupName: user.groupName
      });
      
      const userPermissionsData = {
        role: user.role,
        groupId: user.groupId,
        groupName: user.groupName
      };
      
      console.log('🔧 Setting userPermissions to:', userPermissionsData);
      setUserPermissions(userPermissionsData);
      
      // Load group submission settings if user has a group
      if (user.groupId && settings) {
        console.log('🔍 Looking for group settings for groupId:', user.groupId);
        console.log('🔍 Available settings:', settings.groupSubmissionSettings);
        
        const groupSettings = settings.groupSubmissionSettings?.find(
          setting => setting.groupId === user.groupId && setting.isActive
        );
        console.log('✅ Group settings found:', groupSettings);
        setGroupSubmissionSettings(groupSettings);
      } else {
        console.log('⚠️ No group settings found:', {
          hasGroupId: !!user.groupId,
          hasSettings: !!settings,
          groupSubmissionSettings: settings?.groupSubmissionSettings,
          userGroupId: user.groupId
        });
      }
      
      // Mark permissions as loaded
      console.log('✅ Marking permissions as loaded');
      setPermissionsLoaded(true);
    } catch (error) {
      console.error('❌ Failed to load user permissions:', error);
      console.error('❌ Error response:', error.response?.data);
      // Still mark as loaded to prevent infinite loading
      setPermissionsLoaded(true);
    }
  };

  // Helper functions for permission checks
  const canSubmitApplication = () => {
    // Wait for permissions to be loaded
    if (!permissionsLoaded) return true; // Default to true while loading
    
    // Default: allow submission for all users unless explicitly disabled
    if (!userPermissions) return true;
    
    // Admin can always submit
    if (userPermissions.role === 'admin') return true;
    
    // Check if user has group submission settings
    if (groupSubmissionSettings) {
      return groupSubmissionSettings.submissionAction !== 'disabled';
    }
    
    // Default: allow submission if no specific restrictions
    return true;
  };

  const canGenerateInvoice = () => {
    // Wait for permissions to be loaded
    if (!permissionsLoaded) return true; // Default to true while loading
    
    // Default: allow invoice generation unless explicitly disabled
    if (!userPermissions) return true;
    
    // Admin can always generate invoice
    if (userPermissions.role === 'admin') return true;
    
    // Check group settings
    if (groupSubmissionSettings) {
      return groupSubmissionSettings.autoGenerateInvoice;
    }
    
    // Default: allow invoice generation
    return true;
  };

  const canRedirectToPayment = () => {
    console.log('canRedirectToPayment called:', {
      permissionsLoaded,
      userPermissions,
      groupSubmissionSettings,
      userRole: userPermissions?.role,
      groupId: userPermissions?.groupId,
      redirectToPayment: groupSubmissionSettings?.redirectToPayment
    });
    
    // Wait for permissions to be loaded
    if (!permissionsLoaded) return false; // Default to false while loading
    
    // Default: don't redirect to payment unless explicitly enabled
    if (!userPermissions) return false;
    
    // Admin can always redirect to payment
    if (userPermissions.role === 'admin') return true;
    
    // Check group settings
    if (groupSubmissionSettings) {
      return groupSubmissionSettings.redirectToPayment;
    }
    
    // If user has no group or no group settings, check if they should have payment access
    // For testing: allow payment redirect for users without group settings
    if (!userPermissions.groupId) {
      console.log('User has no group, allowing payment redirect for testing');
      return true;
    }
    
    // For testing: if no group settings found, allow payment redirect
    // This helps when group settings are not configured yet
    console.log('No group settings found, allowing payment redirect for testing');
    return true;
  };

  const getSubmissionActionText = () => {
    console.log('getSubmissionActionText called:', {
      canSubmit: canSubmitApplication(),
      userPermissions,
      groupSubmissionSettings,
      applicationStatus: application?.status
    });
    
    if (!canSubmitApplication()) {
      return 'Submission Not Allowed';
    }
    
    if (groupSubmissionSettings) {
      switch (groupSubmissionSettings.submissionAction) {
        case 'submit_only':
          return 'Submit Application';
        case 'generate_invoice':
          return 'Submit & Generate Invoice';
        case 'generate_invoice_and_redirect':
          return 'Submit & Pay Now';
        default:
          return 'Submit Application';
      }
    }
    
    return 'Submit Application';
  };

  const handleStep1Complete = async (step1Data) => {
    try {
      console.log('handleStep1Complete called with:', step1Data);
      setSaving(true);

      // Store the issueToClient option
      if (step1Data.issueToClient !== undefined) {
        setIssueToClient(step1Data.issueToClient);
      }

      if (id) {
        // Update existing application
        console.log('Updating existing application with ID:', id);
        const response = await api.put(`/trademark-applications/${id}`, step1Data);
        console.log('Update response:', response.data);
        setApplication(response.data.data);
      } else {
        // Create new application
        console.log('Creating new application');
        const response = await api.post('/trademark-applications', step1Data);
        console.log('Create response:', response.data);
        setApplication(response.data.data);
        navigate(`/trademark-applications/${response.data.data._id}`);
      }

      setCurrentStep(2);
      toast.success('Step 1 completed successfully');
    } catch (error) {
      console.error('Failed to save step 1:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.message || 'Failed to save step 1');
    } finally {
      setSaving(false);
    }
  };

  const handleStep2Complete = async (step2Data) => {
    try {
      console.log('handleStep2Complete called with:', step2Data);
      setSaving(true);

      const response = await api.put(`/trademark-applications/${id}`, step2Data);
      console.log('Step 2 save response:', response.data);
      setApplication(response.data.data);

      // Proceed to Step 3 without document validation
      setCurrentStep(3);
      setFees(calculateTotalApplicationFees());
      toast.success('Step 2 completed successfully');
    } catch (error) {
      console.error('Failed to save step 2:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      toast.error(error.response?.data?.message || 'Failed to save step 2');
    } finally {
      setSaving(false);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      toast.info(`Moved to Step ${currentStep - 1}`);
    }
  };

  const handleStepNavigation = (targetStep) => {
    // Only allow navigation to completed steps or the next available step
    const maxAllowedStep = Math.max(1, currentStep);

    if (targetStep <= maxAllowedStep) {
      setCurrentStep(targetStep);
      toast.info(`Moved to Step ${targetStep}`);
    } else {
      toast.warning('Please complete the current step before proceeding');
    }
  };

  const updateApplicationData = async (newData) => {
    // Update application data while preserving current step
    setApplication(prevApplication => ({
      ...newData,
      currentStep: prevApplication?.currentStep || newData.currentStep || 1
    }));
  };

  const handleSubmitApplication = async () => {
    try {
      setSaving(true);

      const response = await api.post(`/trademark-applications/${id}/submit`, {
        issueToClient: issueToClient // Pass the invoice client option
      });
      setApplication(response.data.data);

      // Handle post-submission actions based on group settings
      const { postSubmission } = response.data;
      
      if (postSubmission) {
        if (postSubmission.invoiceGenerated) {
          toast.success('Trademark application submitted successfully! Invoice has been generated.');
          setInvoiceData(postSubmission.invoiceData);
          
          // Determine next step based on group settings
          if (postSubmission.redirectToPayment && canRedirectToPayment()) {
            // Go directly to payment step
            setCurrentStep(5);
            setShowPaymentStep(true);
          } else if (canGenerateInvoice()) {
            // Go to invoice step
            setCurrentStep(4);
            setShowInvoiceStep(true);
          } else {
            // Just show success message
            setSubmissionComplete(true);
          }
        } else {
          toast.success('Trademark application submitted successfully!');
          setSubmissionComplete(true);
        }
      } else {
        // Fallback to default behavior
        toast.success('Trademark application submitted successfully!');
        setSubmissionComplete(true);
      }
    } catch (error) {
      console.error('Failed to submit application:', error);
      toast.error(error.response?.data?.message || 'Failed to submit application');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      const response = await api.put(`/trademark-applications/${id}`, {
        status: 'draft'
      });
      setApplication(response.data.data);
      toast.success('Draft saved successfully');
    } catch (error) {
      console.error('Failed to save draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  // Payment processing functions
  const createPayment = async (gateway) => {
    try {
      setPaymentLoading(true);
      setSelectedPaymentGateway(gateway);

      const paymentData = {
        applicationId: id,
        gateway,
        amount: invoiceData?.amount || application?.totalFees,
        description: `Payment for Trademark Application: ${application?.applicationNumber}`
      };

      const response = await api.post('/payments', paymentData);
      const payment = response.data.data;
      
      setCurrentPayment(payment);
      
      // Handle different payment gateways
      switch (gateway) {
        case 'stripe':
          handleStripePayment(payment);
          break;
        case 'paypal':
          handlePayPalPayment(payment);
          break;
        case 'razorpay':
          handleRazorpayPayment(payment);
          break;
        case 'bank_transfer':
          handleBankTransferPayment(payment);
          break;
        default:
          toast.error('Invalid payment gateway');
      }
    } catch (error) {
      console.error('Failed to create payment:', error);
      toast.error(error.response?.data?.message || 'Failed to create payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleStripePayment = async (payment) => {
    try {
      setPaymentStatus('processing');
      
      // Get payment data from backend
      const response = await api.get(`/payments/${payment.paymentId || payment._id}`);
      const paymentData = response.data.data;
      
      if (paymentData.metadata && paymentData.metadata.clientSecret) {
        // Redirect to Stripe payment page or use Stripe Elements
        // For now, we'll simulate the redirect
        toast.info('Redirecting to Stripe payment page...');
        
        // In a real implementation, you would:
        // 1. Load Stripe.js
        // 2. Create payment element
        // 3. Handle payment confirmation
        
        // Simulate payment completion for demo
        setTimeout(async () => {
          await checkPaymentStatus(payment.paymentId || payment._id);
        }, 3000);
      } else {
        toast.error('Payment data not available');
        setPaymentStatus('failed');
      }
    } catch (error) {
      console.error('Stripe payment error:', error);
      setPaymentStatus('failed');
      toast.error('Payment failed. Please try again.');
    }
  };

  const handlePayPalPayment = async (payment) => {
    try {
      setPaymentStatus('processing');
      
      // Get payment data from backend
      const response = await api.get(`/payments/${payment.paymentId || payment._id}`);
      const paymentData = response.data.data;
      
      if (paymentData.metadata && paymentData.metadata.approvalUrl) {
        // Redirect to PayPal payment page
        window.open(paymentData.metadata.approvalUrl, '_blank');
        
        // Check payment status periodically
        const checkInterval = setInterval(async () => {
          const statusResponse = await api.get(`/payments/${payment.paymentId || payment._id}`);
          const statusData = statusResponse.data.data;
          
          if (statusData.status === 'completed') {
            clearInterval(checkInterval);
            setPaymentStatus('completed');
            toast.success('Payment completed successfully!');
          } else if (statusData.status === 'failed') {
            clearInterval(checkInterval);
            setPaymentStatus('failed');
            toast.error('Payment failed. Please try again.');
          }
        }, 5000); // Check every 5 seconds
        
        // Stop checking after 10 minutes
        setTimeout(() => {
          clearInterval(checkInterval);
        }, 600000);
      } else {
        toast.error('PayPal payment data not available');
        setPaymentStatus('failed');
      }
    } catch (error) {
      console.error('PayPal payment error:', error);
      setPaymentStatus('failed');
      toast.error('Payment failed. Please try again.');
    }
  };

  const handleRazorpayPayment = async (payment) => {
    try {
      setPaymentStatus('processing');
      
      // Get payment data from backend
      const response = await api.get(`/payments/${payment.paymentId || payment._id}`);
      const paymentData = response.data.data;
      
      if (paymentData.metadata && paymentData.metadata.orderId) {
        // Load Razorpay script if not already loaded
        if (!window.Razorpay) {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = () => initializeRazorpayPayment(paymentData);
          document.head.appendChild(script);
        } else {
          initializeRazorpayPayment(paymentData);
        }
      } else {
        toast.error('Razorpay payment data not available');
        setPaymentStatus('failed');
      }
    } catch (error) {
      console.error('Razorpay payment error:', error);
      setPaymentStatus('failed');
      toast.error('Payment failed. Please try again.');
    }
  };

  const initializeRazorpayPayment = (paymentData) => {
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_YOUR_KEY_ID',
      amount: paymentData.amount * 100, // Razorpay expects amount in paise
      currency: paymentData.currency,
      order_id: paymentData.metadata.orderId,
      name: 'Trademark Application Payment',
      description: paymentData.description,
      handler: async function (response) {
        // Payment successful
        try {
          await checkPaymentStatus(paymentData.paymentId);
        } catch (error) {
          console.error('Error checking payment status:', error);
        }
      },
      prefill: {
        name: application?.applicantName || '',
        email: application?.applicantEmail || ''
      },
      theme: {
        color: '#3B82F6'
      }
    };
    
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const handleBankTransferPayment = (payment) => {
    // Bank transfer shows instructions
    setPaymentStatus('pending');
    toast.info('Bank transfer details provided. Please complete the transfer.');
  };

  const checkPaymentStatus = async (paymentId) => {
    try {
      const response = await api.get(`/payments/${paymentId}`);
      const payment = response.data.data;
      
      setCurrentPayment(payment);
      setPaymentStatus(payment.status);
      
      if (payment.status === 'completed') {
        toast.success('Payment completed successfully!');
        
        // Refresh invoice data to show converted invoice
        if (invoiceData && invoiceData.proformaInvoiceId) {
          try {
            // Check if proforma invoice has been converted
            const invoiceResponse = await api.get(`/invoices/${invoiceData.proformaInvoiceId}`);
            const invoice = invoiceResponse.data.data;
            
            if (invoice.convertedTo) {
              // Proforma has been converted, get the new invoice
              const convertedInvoiceResponse = await api.get(`/invoices/${invoice.convertedTo}`);
              const convertedInvoice = convertedInvoiceResponse.data.data;
              
              // Update invoice data to show the converted invoice
              setInvoiceData({
                ...invoiceData,
                invoiceId: convertedInvoice._id,
                invoiceNumber: convertedInvoice.invoiceNumber,
                status: convertedInvoice.status,
                paymentStatus: convertedInvoice.paymentStatus,
                convertedFrom: invoiceData.proformaInvoiceId
              });
              
              toast.info('Proforma invoice has been converted to regular invoice');
            }
          } catch (error) {
            console.error('Error checking invoice conversion:', error);
          }
        }
      } else if (payment.status === 'failed') {
        toast.error('Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
    }
  };

  const cancelPayment = async (paymentId) => {
    try {
      await api.put(`/payments/${paymentId}/cancel`);
      setPaymentStatus('cancelled');
      setCurrentPayment(null);
      toast.success('Payment cancelled successfully');
    } catch (error) {
      console.error('Failed to cancel payment:', error);
      toast.error('Failed to cancel payment');
    }
  };

  // Calculate fees based on trademark settings and class count
  const calculateFees = (filingType) => {
    filingType = filingType?.toLowerCase();
    console.log("Calculating fees for:", filingType);
    if (!settings || !settings.pricing) {
      return {
        governmentFees: 0,
        professionalFees: 0,
        udyogAadhaarFees: 0,
        totalFees: 0
      };
    }

    let baseFees = { governmentFees: 0, professionalFees: 0, udyogAadhaarFees: 0 };

    if (filingType === 'international') {
      // International filing
      baseFees = {
        governmentFees: settings.pricing.international?.governmentFees || 0,
        professionalFees: settings.pricing.international?.professionalFees || 0,
        udyogAadhaarFees: 0
      };
    } else {
      // India filing - get fees based on filing type
      console.log("India filing - getting fees based on filing type:", filingType);

      // Map filing type to database field name
      let pricingKey = filingType;
      if (filingType === 'small enterprise') {
        pricingKey = 'smallEnterprise';
      }

      const indiaPricing = settings.pricing.india?.[pricingKey] || {};
      baseFees = {
        governmentFees: indiaPricing.governmentFees || 0,
        professionalFees: indiaPricing.professionalFees || 0,
        udyogAadhaarFees: indiaPricing.udyogAadhaarFees || 0
      };
    }

    // Multiply by class count
    const calculatedFees = {
      governmentFees: baseFees.governmentFees,
      professionalFees: baseFees.professionalFees,
      udyogAadhaarFees: application.udyogAadhaarAssistance ? baseFees.udyogAadhaarFees : 0
    };

    // Calculate total
    calculatedFees.totalFeesForOneTrademark = calculatedFees.governmentFees + calculatedFees.professionalFees +
      (application.udyogAadhaarAssistance ? calculatedFees.udyogAadhaarFees : 0);

    return calculatedFees;
  };

  // Calculate total application fees
  const calculateTotalApplicationFees = () => {
    if (!application || !application.trademarks) return { governmentFees: 0, professionalFees: 0, udyogAadhaarFees: 0, totalFees: 0 };

    let totalGovernmentFees = 0;
    let totalProfessionalFees = 0;
    let totalUdyogAadhaarFees = 0;
    const fees = calculateFees(application.filingType);
    application.trademarks.forEach(trademark => {
      const classCount = trademark.classes?.length || (trademark.class ? 1 : 0);
      totalGovernmentFees += fees.governmentFees * classCount;
      totalProfessionalFees += fees.professionalFees * classCount;
      totalUdyogAadhaarFees += fees.udyogAadhaarFees * classCount;
    });

    return {
      governmentFeesForOneTrademark: fees.governmentFees,
      professionalFeesForOneTrademark: fees.professionalFees,
      udyogAadhaarFeesForOneTrademark: fees.udyogAadhaarFees,
      totalFeesForOneTrademark: fees.totalFeesForOneTrademark,
      governmentFees: totalGovernmentFees,
      professionalFees: totalProfessionalFees,
      udyogAadhaarFees: totalUdyogAadhaarFees,
      totalFees: totalGovernmentFees + totalProfessionalFees + totalUdyogAadhaarFees
    };
  };

  const getStepStatus = (step) => {
    if (step < currentStep) return 'completed';
    if (step === currentStep) return 'current';
    return 'upcoming';
  };

  const getStepIcon = (step, status) => {
    if (status === 'completed') {
      return (
        <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      );
    }
    
    const icons = {
      1: '📋',
      2: '📄',
      3: '✅',
      4: '🧾',
      5: '💳'
    };
    
    return <span className="text-2xl">{icons[step] || '📝'}</span>;
  };

  const getStepTitle = (step) => {
    const titles = {
      1: 'Application Details',
      2: 'Documents & Trademarks',
      3: 'Review & Submit',
      4: 'Invoice',
      5: 'Payment'
    };
    return titles[step] || `Step ${step}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/trademark-applications')}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ChevronLeftIcon className="h-5 w-5 mr-1" />
                Back to Applications
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {id ? 'Edit Trademark Application' : 'New Trademark Application'}
                </h1>
                {application && (
                  <p className="text-sm text-gray-600">
                    Application Number: {application.applicationNumber}
                  </p>
                )}
              </div>
            </div>

            {application && (
              <div className="flex items-center space-x-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${application.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                  application.status === 'submitted' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                  {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                </span>

                {application.status === 'draft' && (
                  <>
                    {/* Previous Step Button */}
                    {currentStep > 1 && (
                      <button
                        onClick={handlePreviousStep}
                        disabled={saving}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Previous Step
                      </button>
                    )}

                    <button
                      onClick={handleSaveDraft}
                      disabled={saving}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      {saving ? 'Saving...' : 'Save Draft'}
                    </button>

                    {currentStep === 3 && (
                      <button
                        onClick={handleSubmitApplication}
                        disabled={saving}
                        className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        {saving ? 'Submitting...' : 'Submit Application'}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            {[1, 2, 3].map((step) => {
              const status = getStepStatus(step);
              const isClickable = step <= Math.max(1, currentStep);
              const isCurrentStep = step === currentStep;

              return (
                <div key={step} className="flex items-center">
                  <div
                    className={`flex items-center cursor-pointer transition-colors duration-200 ${isClickable
                      ? 'hover:bg-gray-50 rounded-lg p-2'
                      : 'cursor-not-allowed opacity-60'
                      }`}
                    onClick={() => isClickable && !isCurrentStep && handleStepNavigation(step)}
                    title={isClickable ? `Click to go to Step ${step}` : 'Complete current step first'}
                  >
                    {getStepIcon(step, status)}
                    <div className="ml-3">
                      <p className={`text-sm font-medium ${status === 'completed' ? 'text-green-600' :
                        status === 'current' ? 'text-blue-600' :
                          'text-gray-500'
                        }`}>
                        {getStepTitle(step)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {step === 1 ? 'Basic Information' :
                          step === 2 ? 'Trademark Details' :
                            'Review & Submit'}
                      </p>
                    </div>
                  </div>
                  {step < 3 && (
                    <ChevronRightIcon className="h-5 w-5 text-gray-400 mx-4" />
                  )}
                </div>
              );
            })}
            
            {/* Show Invoice Step if enabled */}
            {canGenerateInvoice() && (
              <div className="flex items-center">
                <ChevronRightIcon className="h-5 w-5 text-gray-400 mx-4" />
                <div
                  className={`flex items-center cursor-pointer transition-colors duration-200 ${
                    currentStep >= 4 ? 'hover:bg-gray-50 rounded-lg p-2' : 'cursor-not-allowed opacity-60'
                  }`}
                  onClick={() => currentStep >= 4 && currentStep !== 4 && handleStepNavigation(4)}
                  title={currentStep >= 4 ? 'Click to go to Invoice' : 'Complete previous steps first'}
                >
                  {getStepIcon(4, getStepStatus(4))}
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${getStepStatus(4) === 'completed' ? 'text-green-600' :
                      getStepStatus(4) === 'current' ? 'text-blue-600' :
                        'text-gray-500'
                      }`}>
                      {getStepTitle(4)}
                    </p>
                    <p className="text-xs text-gray-500">Invoice Details</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Show Payment Step if enabled */}
            {canRedirectToPayment() && (
              <div className="flex items-center">
                <ChevronRightIcon className="h-5 w-5 text-gray-400 mx-4" />
                <div
                  className={`flex items-center cursor-pointer transition-colors duration-200 ${
                    currentStep >= 5 ? 'hover:bg-gray-50 rounded-lg p-2' : 'cursor-not-allowed opacity-60'
                  }`}
                  onClick={() => currentStep >= 5 && currentStep !== 5 && handleStepNavigation(5)}
                  title={currentStep >= 5 ? 'Click to go to Payment' : 'Complete previous steps first'}
                >
                  {getStepIcon(5, getStepStatus(5))}
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${getStepStatus(5) === 'completed' ? 'text-green-600' :
                      getStepStatus(5) === 'current' ? 'text-blue-600' :
                        'text-gray-500'
                      }`}>
                      {getStepTitle(5)}
                    </p>
                    <p className="text-xs text-gray-500">Payment Gateway</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {currentStep === 1 && (
          <TrademarkStep1
            application={application}
            settings={settings}
            masterDataList={masterDataList}
            onComplete={handleStep1Complete}
            saving={saving}
            onPreviousStep={currentStep > 1 ? handlePreviousStep : undefined}
          />
        )}

        {currentStep === 2 && (
          <TrademarkStep2
            application={application}
            settings={settings}
            onComplete={handleStep2Complete}
            saving={saving}
            onPreviousStep={handlePreviousStep}
          />
        )}

        {currentStep === 3 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Review & Submit</h2>

            {/* Application Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Application Details</h3>
                <dl className="space-y-2">
                  {/* <div>
                    <dt className="text-sm font-medium text-gray-500">Application Number</dt>
                    <dd className="text-sm text-gray-900">{application.applicationNumber}</dd>
                  </div> */}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Filing Type</dt>
                    <dd className="text-sm text-gray-900">{application.filingType}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Country</dt>
                    <dd className="text-sm text-gray-900">{application.country}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Entity Type</dt>
                    <dd className="text-sm text-gray-900">
                      {clientTypesLoading ? (
                        <span className="text-gray-500">Loading...</span>
                      ) : (
                        (() => {
                          const foundType = clientTypes.find(type => type._id === application.entityType);
                          return foundType?.name || application.entityType || 'N/A';
                        })()
                      )}
                    </dd>
                  </div>
                  {application.proprietorFirmName && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Proprietor/Firm Name</dt>
                      <dd className="text-sm text-gray-900">{application.proprietorFirmName}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Udyog Aadhaar Assistance</dt>
                    <dd className="text-sm text-gray-900">
                      {application.udyogAadhaarAssistance ? 'Yes' : 'No'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Total Trademarks</dt>
                    <dd className="text-sm text-gray-900">
                      {application.trademarks.length > 0 ?
                        (application.trademarks.length === 1 ? '1 trademark' : `${application.trademarks.length} trademarks`) :
                        'No trademarks'
                      }

                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Total Classes</dt>
                    <dd className="text-sm text-gray-900">
                      {(() => {
                        const totalClasses = application.trademarks.reduce((total, trademark) => {
                          if (trademark.classes && trademark.classes.length > 0) {
                            return total + trademark.classes.length;
                          } else if (trademark.class) {
                            return total + 1;
                          }
                          return total;
                        }, 0);
                        return totalClasses;
                      })()}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Applicant Information</h3>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Client Name</dt>
                    <dd className="text-sm text-gray-900">{application.masterDataId?.clientName || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Client Address</dt>
                    <dd className="text-sm text-gray-900">{application.masterDataId?.clientAddress || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Country</dt>
                    <dd className="text-sm text-gray-900">{application.country || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">State</dt>
                    <dd className="text-sm text-gray-900">{application.masterDataId?.state || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Region</dt>
                    <dd className="text-sm text-gray-900">{application.masterDataId?.region || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Pincode</dt>
                    <dd className="text-sm text-gray-900">{application.masterDataId?.pincode || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Entity Type</dt>
                    <dd className="text-sm text-gray-900">
                      {clientTypesLoading ? (
                        <span className="text-gray-500">Loading...</span>
                      ) : (
                        (() => {
                          const foundType = clientTypes.find(type => type._id === application.entityType);
                          return foundType?.name || application.entityType || 'N/A';
                        })()
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Applicant Type</dt>
                    <dd className="text-sm text-gray-900">{application.masterDataId?.applicantType || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Annual Turnover</dt>
                    <dd className="text-sm text-gray-900">
                      {application.masterDataId?.turnover ?
                        `₹${(application.masterDataId.turnover / 10000000).toFixed(1)} Crore` :
                        'N/A'
                      }
                    </dd>
                  </div>
                  {application.masterDataId?.contactPersons && application.masterDataId.contactPersons.length > 0 && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Primary Contact</dt>
                      <dd className="text-sm text-gray-900">
                        {application.masterDataId.contactPersons[0].title} {application.masterDataId.contactPersons[0].name}
                        <br />
                        <span className="text-gray-500">
                          {application.masterDataId.contactPersons[0].email}
                        </span>
                      </dd>
                    </div>
                  )}
                  {application.masterDataId?.authorizedSignatories && application.masterDataId.authorizedSignatories.length > 0 && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Authorized Signatory</dt>
                      <dd className="text-sm text-gray-900">
                        {application.masterDataId.authorizedSignatories[0].title} {application.masterDataId.authorizedSignatories[0].designationName}
                        <br />
                        <span className="text-gray-500">
                          Son of {application.masterDataId.authorizedSignatories[0].fatherName}
                        </span>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>


            </div>

            {/* Trademarks List */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Trademarks</h3>

              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Fee Summary</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 flex flex-col items-center shadow-sm">
                    <span className="text-xs text-blue-700 font-medium mb-1">Total Fees</span>
                    <span className="text-2xl font-bold text-blue-900">₹{fees.totalFees.toLocaleString()}</span>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 flex flex-col items-center shadow-sm">
                    <span className="text-xs text-green-700 font-medium mb-1">Government Fees</span>
                    <span className="text-xl font-semibold text-green-900">₹{fees.governmentFees.toLocaleString()}</span>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4 flex flex-col items-center shadow-sm">
                    <span className="text-xs text-yellow-700 font-medium mb-1">Professional Fees</span>
                    <span className="text-xl font-semibold text-yellow-900">₹{fees.professionalFees.toLocaleString()}</span>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 flex flex-col items-center shadow-sm">
                    <span className="text-xs text-purple-700 font-medium mb-1">Udyog Aadhaar Fees</span>
                    <span className="text-xl font-semibold text-purple-900">₹{fees.udyogAadhaarFees.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                {application.trademarks.map((trademark, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-1">
                          Trademark {index + 1}: {trademark.trademarkName}
                        </h4>
                        <p className="text-sm text-gray-600">
                          Type: {trademark.trademarkType}
                        </p>
                      </div>
                      {/* <div className="text-right">
                        {(() => {
                          const classCount = trademark.classes?.length || (trademark.class ? 1 : 0);
                          const fees = calculateFees(application.filingType, application.entityType, classCount);
                          return (
                            <>
                              <span className="text-lg font-semibold text-gray-900">
                                ₹{fees.totalFees.toLocaleString()}
                              </span>
                              <div className="text-xs text-gray-500 mt-1">
                                Govt: ₹{fees.governmentFees.toLocaleString()} | Prof: ₹{fees.professionalFees.toLocaleString()}
                                {fees.udyogAadhaarFees > 0 && ` | Udyog: ₹${fees.udyogAadhaarFees.toLocaleString()}`}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                {classCount} class{classCount !== 1 ? 'es' : ''}
                              </div>
                            </>
                          );
                        })()}
                      </div> */}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">


                      {/* Classes and Goods/Services */}
                      <div>
                        <h5 className="font-medium text-gray-900 mb-3">Classes & Goods/Services</h5>
                        <dl className="space-y-2">
                          {/* Legacy single class info */}
                          {trademark.class && !trademark.classes?.length && (
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Class</dt>
                              <dd className="text-sm text-gray-900">
                                Class {trademark.class} - {trademark.classDescription}
                              </dd>
                            </div>
                          )}

                          {/* Multiple classes */}
                          {trademark.classes && trademark.classes.length > 0 && (
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Classes</dt>
                              <dd className="text-sm text-gray-900">
                                <div className="space-y-1">
                                  {trademark.classes.map((classItem, classIndex) => (
                                    <div key={classIndex} className="bg-gray-50 p-2 rounded">
                                      <span className="font-medium">Class {classItem.class}:</span> {classItem.classDescription}
                                      <div className="text-xs text-gray-500 mt-1">
                                        Govt: ₹{fees.governmentFeesForOneTrademark} | Prof: ₹{fees.professionalFeesForOneTrademark}
                                        {fees.udyogAadhaarFeesForOneTrademark > 0 && ` | Udyog: ₹${fees.udyogAadhaarFeesForOneTrademark}`}
                                      </div>
                                    </div>
                                  ))}
                                  <div className="bg-gray-50 p-2 rounded">
                                    <span className="font-medium">Total Fees For This Trademark:</span> ₹{fees.totalFeesForOneTrademark * trademark.classes.length}
                                    <div className="text-xs text-gray-500 mt-1">
                                      Govt: ₹{fees.governmentFeesForOneTrademark * trademark.classes.length} | Prof: ₹{fees.professionalFeesForOneTrademark * trademark.classes.length}
                                      {fees.udyogAadhaarFeesForOneTrademark > 0 && ` | Udyog: ₹${fees.udyogAadhaarFeesForOneTrademark * trademark.classes.length}`}
                                    </div>
                                  </div>
                                </div>
                              </dd>
                            </div>
                          )}

                          {trademark.goodsAndServices && (
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Goods & Services</dt>
                              <dd className="text-sm text-gray-900">{trademark.goodsAndServices}</dd>
                            </div>
                          )}
                        </dl>
                      </div>

                      {/* Basic Information */}
                      <div>
                        <h5 className="font-medium text-gray-900 mb-3">Basic Information</h5>
                        <dl className="space-y-2">
                          {trademark.description && (
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Description</dt>
                              <dd className="text-sm text-gray-900">{trademark.description}</dd>
                            </div>
                          )}
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Language</dt>
                            <dd className="text-sm text-gray-900">{trademark.language || 'English'}</dd>
                          </div>
                          {trademark.transliteration && (
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Transliteration</dt>
                              <dd className="text-sm text-gray-900">{trademark.transliteration}</dd>
                            </div>
                          )}
                          {trademark.translation && (
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Translation</dt>
                              <dd className="text-sm text-gray-900">{trademark.translation}</dd>
                            </div>
                          )}
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Use of Mark</dt>
                            <dd className="text-sm text-gray-900">{trademark.useOfMark || 'Proposed to be used'}</dd>
                          </div>
                          {trademark.proposedUseDate && (
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Proposed Use Date</dt>
                              <dd className="text-sm text-gray-900">
                                {new Date(trademark.proposedUseDate).toLocaleDateString()}
                              </dd>
                            </div>
                          )}
                          {trademark.conditionsAndLimitations && (
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Conditions & Limitations</dt>
                              <dd className="text-sm text-gray-900">{trademark.conditionsAndLimitations}</dd>
                            </div>
                          )}
                        </dl>
                      </div>
                    </div>

                    {/* Documents */}
                    {trademark.documents && trademark.documents.length > 0 && (
                      <div className="mt-6">
                        <h5 className="font-medium text-gray-900 mb-3">Documents</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {trademark.documents.map((doc, docIndex) => (
                            <div key={docIndex} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                              <div className={`w-2 h-2 rounded-full ${doc.uploaded ? 'bg-green-500' : 'bg-red-500'
                                }`}></div>
                              <span className="text-sm text-gray-700">{doc.type}</span>
                              {doc.required && (
                                <span className="text-xs text-red-600 font-medium">Required</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Application Documents */}
            {application.documents && (
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Application Documents</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(application.documents).map(([docType, docInfo]) => (
                    <div key={docType} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 capitalize">
                          {docType.replace(/([A-Z])/g, ' $1').trim()}
                        </h4>
                        <div className={`w-3 h-3 rounded-full ${docInfo.uploaded ? 'bg-green-500' : 'bg-red-500'
                          }`}></div>
                      </div>
                      <div className="text-sm text-gray-600">
                        {docInfo.required ? (
                          <span className="text-red-600 font-medium">Required</span>
                        ) : (
                          <span className="text-gray-500">Optional</span>
                        )}
                      </div>
                      {docInfo.uploaded && (
                        <div className="text-xs text-green-600 mt-1">
                          ✓ Uploaded
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between">
              <button
                onClick={handlePreviousStep}
                disabled={saving}
                className="px-6 py-3 border border-gray-300 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Previous Step
              </button>
              
              {/* Show group submission info if available */}
              {groupSubmissionSettings && (
                <div className="flex-1 mx-4 flex items-center justify-center">
                  <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md">
                    <span className="font-medium">Group:</span> {groupSubmissionSettings.groupName} • 
                    <span className="font-medium ml-2">Action:</span> {groupSubmissionSettings.submissionAction.replace(/_/g, ' ')}
                    {groupSubmissionSettings.autoGenerateInvoice && (
                      <span className="text-green-600 ml-2">• Auto Invoice</span>
                    )}
                    {groupSubmissionSettings.redirectToPayment && (
                      <span className="text-blue-600 ml-2">• Payment Redirect</span>
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex space-x-3">
                {/* Show View Invoice button if application is submitted and invoice generation is enabled */}
                {(() => {
                  const shouldShowViewInvoice = application.status === 'submitted' && canGenerateInvoice();
                  console.log('View Invoice button logic:', {
                    applicationStatus: application?.status,
                    canGenerateInvoice: canGenerateInvoice(),
                    shouldShowViewInvoice,
                    invoiceData: !!invoiceData,
                    permissionsLoaded
                  });
                  return shouldShowViewInvoice;
                })() && (
                  <button
                    onClick={async () => {
                      // If invoiceData doesn't exist, load it first
                      if (!invoiceData) {
                        try {
                          const response = await api.get(`/trademark-applications/${id}/invoice-data`);
                          setInvoiceData(response.data.data);
                        } catch (error) {
                          console.error('Failed to load invoice data:', error);
                          toast.error('Failed to load invoice data');
                          return;
                        }
                      }
                      setCurrentStep(4);
                      setShowInvoiceStep(true);
                    }}
                    className="px-6 py-3 bg-green-600 border border-transparent rounded-md text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    View Invoice
                  </button>
                )}
                
                {/* Submit button - only show if application is not submitted */}
                {application.status !== 'submitted' && (
                  <button
                    onClick={handleSubmitApplication}
                    disabled={saving || !canSubmitApplication()}
                    className={`px-6 py-3 border border-transparent rounded-md text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 ${
                      canSubmitApplication() 
                        ? 'bg-blue-600 text-white hover:bg-blue-700' 
                        : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    }`}
                  >
                    {saving ? 'Submitting...' : getSubmissionActionText()}
                  </button>
                )}
                
                {/* Loading indicator for permissions */}
                {!permissionsLoaded && (
                  <div className="flex items-center px-6 py-3">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    <span className="text-sm text-gray-600">Loading permissions...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            {!invoiceData ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading invoice data...</p>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Invoice</h2>

                {/* Invoice Header */}
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Invoice Details</h4>
                      <p className="text-sm text-gray-600">Application: {invoiceData.applicationNumber}</p>
                      <p className="text-sm text-gray-600">Date: {new Date().toLocaleDateString()}</p>
                      <p className="text-sm text-gray-600">Due Date: {new Date(invoiceData.dueDate).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Client Information</h4>
                      <p className="text-sm text-gray-600">{invoiceData.clientName}</p>
                      <p className="text-sm text-gray-600">{invoiceData.clientEmail}</p>
                      {invoiceData.clientAddress && (
                        <p className="text-sm text-gray-600">{invoiceData.clientAddress}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Issuer Information */}
                {invoiceData.issuer && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Issued By</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-medium text-gray-900">{invoiceData.issuer.companyName}</h5>
                          <p className="text-sm text-gray-600 whitespace-pre-line">{invoiceData.issuer.address}</p>
                        </div>
                        <div>
                          <div className="space-y-1">
                            {invoiceData.issuer.phone && (
                              <p className="text-sm text-gray-600">📞 {invoiceData.issuer.phone}</p>
                            )}
                            {invoiceData.issuer.email && (
                              <p className="text-sm text-gray-600">✉️ {invoiceData.issuer.email}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Invoice Items */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Items</h4>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {invoiceData.items.map((item, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.description}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.quantity}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{item.unitPrice.toLocaleString()}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{item.total.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Fee Breakdown */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Fee Breakdown</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Government Fees:</span>
                        <span className="text-sm font-medium">₹{invoiceData.breakdown.governmentFees.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Professional Fees:</span>
                        <span className="text-sm font-medium">₹{invoiceData.breakdown.professionalFees.toLocaleString()}</span>
                      </div>
                      {invoiceData.breakdown.udyogAadhaarFees > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Udyog Aadhaar Fees:</span>
                          <span className="text-sm font-medium">₹{invoiceData.breakdown.udyogAadhaarFees.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="border-t border-gray-200 pt-2">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-900">Total Amount:</span>
                          <span className="text-lg font-bold text-gray-900">₹{invoiceData.amount.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-between">
                  <button
                    onClick={() => setCurrentStep(3)}
                    className="px-6 py-3 border border-gray-300 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Previous Step
                  </button>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        // Download invoice as PDF
                        const downloadPdf = async () => {
                          try {
                            const response = await api.get(`/trademark-applications/${id}/invoice-pdf`, {
                              responseType: 'blob'
                            });
                            
                            // Create a blob URL and trigger download
                            const blob = new Blob([response.data], { type: 'application/pdf' });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `invoice-${application.applicationNumber}.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                            
                            toast.success('Invoice PDF downloaded successfully!');
                          } catch (error) {
                            console.error('Failed to download PDF:', error);
                            toast.error('Failed to download PDF');
                          }
                        };
                        
                        downloadPdf();
                      }}
                      className="px-6 py-3 bg-green-600 border border-transparent rounded-md text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      Download PDF
                    </button>
                    
                    {(() => {
                      const shouldShowPaymentButton = canRedirectToPayment();
                      console.log('Proceed to Payment button logic:', {
                        canRedirectToPayment: canRedirectToPayment(),
                        userPermissions,
                        groupSubmissionSettings,
                        shouldShowPaymentButton,
                        permissionsLoaded
                      });
                      return shouldShowPaymentButton;
                    })() && (
                      <button
                        onClick={() => setCurrentStep(5)}
                        className="px-6 py-3 bg-blue-600 border border-transparent rounded-md text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Proceed to Payment
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {currentStep === 5 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Payment</h2>
            
            {/* Payment Status */}
            {paymentStatus && (
              <div className="mb-6">
                <div className={`p-4 rounded-lg ${
                  paymentStatus === 'completed' ? 'bg-green-50 border border-green-200' :
                  paymentStatus === 'pending' ? 'bg-yellow-50 border border-yellow-200' :
                  paymentStatus === 'processing' ? 'bg-blue-50 border border-blue-200' :
                  'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex">
                    <div className="flex-shrink-0">
                      {paymentStatus === 'completed' ? (
                        <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : paymentStatus === 'pending' ? (
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                      ) : paymentStatus === 'processing' ? (
                        <svg className="h-5 w-5 text-blue-400 animate-spin" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="ml-3">
                      <h3 className={`text-sm font-medium ${
                        paymentStatus === 'completed' ? 'text-green-800' :
                        paymentStatus === 'pending' ? 'text-yellow-800' :
                        paymentStatus === 'processing' ? 'text-blue-800' :
                        'text-red-800'
                      }`}>
                        Payment {paymentStatus.charAt(0).toUpperCase() + paymentStatus.slice(1)}
                      </h3>
                      <div className={`mt-2 text-sm ${
                        paymentStatus === 'completed' ? 'text-green-700' :
                        paymentStatus === 'pending' ? 'text-yellow-700' :
                        paymentStatus === 'processing' ? 'text-blue-700' :
                        'text-red-700'
                      }`}>
                        {paymentStatus === 'completed' ? 'Your payment has been processed successfully.' :
                         paymentStatus === 'pending' ? 'Your payment is pending. Please complete the transfer.' :
                         paymentStatus === 'processing' ? 'Your payment is being processed. Please wait.' :
                         'Payment failed. Please try again.'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Bank Transfer Details */}
            {currentPayment?.bankTransferDetails && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Bank Transfer Details</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Account Number</p>
                      <p className="font-medium text-gray-900">{currentPayment.bankTransferDetails.accountNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Account Name</p>
                      <p className="font-medium text-gray-900">{currentPayment.bankTransferDetails.accountName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Bank Name</p>
                      <p className="font-medium text-gray-900">{currentPayment.bankTransferDetails.bankName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">IFSC Code</p>
                      <p className="font-medium text-gray-900">{currentPayment.bankTransferDetails.ifscCode}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-600">Reference Number</p>
                      <p className="font-medium text-gray-900">{currentPayment.bankTransferDetails.referenceNumber}</p>
                    </div>
                  </div>
                  
                  {currentPayment.instructions && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Instructions:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {currentPayment.instructions.map((instruction, index) => (
                          <li key={index} className="flex items-start">
                            <span className="text-blue-600 mr-2">•</span>
                            {instruction}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payment Amount */}
            <div className="mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Payment Amount</h3>
                <div className="text-3xl font-bold text-gray-900">
                  ₹{invoiceData?.amount?.toLocaleString() || '0'}
                </div>
                <p className="text-sm text-gray-600 mt-1">Total amount due for trademark application</p>
              </div>
            </div>

            {/* Payment Gateways - Only show if no payment is in progress */}
            {!paymentStatus && !paymentLoading && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Select Payment Method</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Stripe */}
                  <div 
                    onClick={() => createPayment('stripe')}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center mr-3">
                        <span className="text-white font-bold text-sm">S</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Stripe</h4>
                        <p className="text-sm text-gray-600">Credit/Debit Cards</p>
                      </div>
                    </div>
                  </div>

                  {/* PayPal */}
                  <div 
                    onClick={() => createPayment('paypal')}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center mr-3">
                        <span className="text-white font-bold text-sm">P</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">PayPal</h4>
                        <p className="text-sm text-gray-600">PayPal Account</p>
                      </div>
                    </div>
                  </div>

                  {/* Razorpay */}
                  <div 
                    onClick={() => createPayment('razorpay')}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-purple-600 rounded flex items-center justify-center mr-3">
                        <span className="text-white font-bold text-sm">R</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Razorpay</h4>
                        <p className="text-sm text-gray-600">UPI & Cards</p>
                      </div>
                    </div>
                  </div>

                  {/* Bank Transfer */}
                  <div 
                    onClick={() => createPayment('bank_transfer')}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-green-600 rounded flex items-center justify-center mr-3">
                        <span className="text-white font-bold text-sm">B</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Bank Transfer</h4>
                        <p className="text-sm text-gray-600">NEFT/RTGS/IMPS</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Loading State */}
            {paymentLoading && (
              <div className="mb-6">
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Processing payment...</p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between">
              <button
                onClick={() => setCurrentStep(4)}
                disabled={paymentLoading}
                className="px-6 py-3 border border-gray-300 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Previous Step
              </button>
              
              <div className="flex space-x-3">
                {currentPayment && paymentStatus === 'pending' && (
                  <button
                    onClick={() => checkPaymentStatus(currentPayment._id)}
                    className="px-6 py-3 bg-green-600 border border-transparent rounded-md text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Check Status
                  </button>
                )}
                
                {currentPayment && paymentStatus === 'pending' && (
                  <button
                    onClick={() => cancelPayment(currentPayment._id)}
                    className="px-6 py-3 bg-red-600 border border-transparent rounded-md text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Cancel Payment
                  </button>
                )}
                
                {paymentStatus === 'completed' && (
                  <button
                    onClick={() => navigate('/trademark-applications')}
                    className="px-6 py-3 bg-blue-600 border border-transparent rounded-md text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Back to Applications
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Display */}
      {showInvoice && invoiceData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">Invoice</h3>
                <button
                  onClick={() => {
                    setShowInvoice(false);
                    setInvoiceData(null);
                    navigate('/trademark-applications');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Invoice Header */}
              <div className="border-b border-gray-200 pb-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Invoice Details</h4>
                    <p className="text-sm text-gray-600">Application: {invoiceData.applicationNumber}</p>
                    <p className="text-sm text-gray-600">Date: {new Date().toLocaleDateString()}</p>
                    <p className="text-sm text-gray-600">Due Date: {new Date(invoiceData.dueDate).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Client Information</h4>
                    <p className="text-sm text-gray-600">{invoiceData.clientName}</p>
                    <p className="text-sm text-gray-600">{invoiceData.clientEmail}</p>
                    {invoiceData.clientAddress && (
                      <p className="text-sm text-gray-600">{invoiceData.clientAddress}</p>
                    )}
                    {console.log('Invoice client data:', {
                      clientName: invoiceData.clientName,
                      clientEmail: invoiceData.clientEmail,
                      clientAddress: invoiceData.clientAddress
                    })}
                  </div>
                </div>
              </div>

              {/* Issuer Information */}
              {invoiceData.issuer && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Issued By</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h5 className="font-medium text-gray-900">{invoiceData.issuer.companyName}</h5>
                        <p className="text-sm text-gray-600 whitespace-pre-line">{invoiceData.issuer.address}</p>
                      </div>
                      <div>
                        <div className="space-y-1">
                          {invoiceData.issuer.phone && (
                            <p className="text-sm text-gray-600">📞 {invoiceData.issuer.phone}</p>
                          )}
                          {invoiceData.issuer.email && (
                            <p className="text-sm text-gray-600">✉️ {invoiceData.issuer.email}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Invoice Items */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Items</h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invoiceData.items.map((item, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.description}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.quantity}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{item.unitPrice.toLocaleString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{item.total.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Fee Breakdown */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Fee Breakdown</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Government Fees:</span>
                      <span className="text-sm font-medium">₹{invoiceData.breakdown.governmentFees.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Professional Fees:</span>
                      <span className="text-sm font-medium">₹{invoiceData.breakdown.professionalFees.toLocaleString()}</span>
                    </div>
                    {invoiceData.breakdown.udyogAadhaarFees > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Udyog Aadhaar Fees:</span>
                        <span className="text-sm font-medium">₹{invoiceData.breakdown.udyogAadhaarFees.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="border-t border-gray-200 pt-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-900">Total Amount:</span>
                        <span className="text-lg font-bold text-gray-900">₹{invoiceData.amount.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Options */}
              {canRedirectToPayment() && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Payment Options</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      onClick={() => {
                        // Navigate to Step 5 for payment options
                        setCurrentStep(5);
                      }}
                      className="flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      Pay Online
                    </button>
                    <button
                      onClick={() => {
                        // Handle offline payment
                        toast.info('Please contact us for offline payment instructions.');
                      }}
                      className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Offline Payment
                    </button>
                  </div>
                </div>
              )}

              {/* Show payment restriction message if payment is not allowed */}
              {!canRedirectToPayment() && (
                <div className="mb-6">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Payment Options Restricted
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>
                            Payment options are not available for your group. Please contact your administrator for payment instructions.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowInvoice(false);
                    setInvoiceData(null);
                    navigate('/trademark-applications');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    // Download invoice as PDF
                    const downloadPdf = async () => {
                      try {
                        const response = await api.get(`/trademark-applications/${id}/invoice-pdf`, {
                          responseType: 'blob'
                        });
                        
                        // Create a blob URL and trigger download
                        const blob = new Blob([response.data], { type: 'application/pdf' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `invoice-${application.applicationNumber}.pdf`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                        
                        toast.success('Invoice PDF downloaded successfully!');
                      } catch (error) {
                        console.error('Failed to download PDF:', error);
                        toast.error('Failed to download PDF');
                      }
                    };
                    
                    downloadPdf();
                  }}
                  className="px-4 py-2 bg-green-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrademarkApplication; 